import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

const PULL_THRESHOLD = 80; // Distance to trigger refresh
const MAX_PULL = 120; // Maximum pull distance

export default function PullToRefresh({ children, disabled = false }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef(null);
  const queryClient = useQueryClient();

  // Only enable on mobile/native, and not on form pages
  const isEnabled = !disabled && (Capacitor.isNativePlatform() || window.innerWidth < 1280);

  const isAtTop = useCallback(() => {
    // Check if scroll position is at top
    return window.scrollY <= 0;
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (!isEnabled || isRefreshing) return;
    if (!isAtTop()) return;

    // Don't start pull if More menu is open
    const moreMenuOpen = document.querySelector('[data-more-menu]');
    if (moreMenuOpen) return;

    // Don't start pull if any modal is open
    const modalOpen = document.querySelector('[data-modal]');
    if (modalOpen) return;

    // Don't start pull if drag is in progress (e.g., category reorder)
    const dragInProgress = document.body.hasAttribute('data-drag-in-progress');
    if (dragInProgress) return;

    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    setIsPulling(true);
  }, [isEnabled, isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;
    if (!isAtTop()) {
      setPullDistance(0);
      return;
    }

    // Stop pulling if drag started
    const dragInProgress = document.body.hasAttribute('data-drag-in-progress');
    if (dragInProgress) {
      setPullDistance(0);
      setIsPulling(false);
      return;
    }

    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    if (diff > 0) {
      // Apply resistance - the further you pull, the harder it gets
      const resistance = 0.5;
      const distance = Math.min(diff * resistance, MAX_PULL);
      setPullDistance(distance);

      // Prevent default scroll when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, isRefreshing, isAtTop]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    setIsPulling(false);

    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      // Check if More menu is open - don't refresh, menu has its own drag handler
      const moreMenuOpen = document.querySelector('[data-more-menu]');
      if (moreMenuOpen) {
        setPullDistance(0);
        return;
      }

      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD); // Keep indicator visible

      try {
        // Invalidate all queries to mark them stale, then refetch
        // This ensures fresh API calls instead of using cached data
        await queryClient.invalidateQueries();
        await queryClient.refetchQueries({ type: 'active' });
        // Reset dashboard filters on refresh
        window.dispatchEvent(new Event('dashboard-refresh'));
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        // Animate out
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 300);
      }
    } else {
      // Reset if not pulled enough
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, isRefreshing, queryClient]);

  useEffect(() => {
    if (!isEnabled) return;

    const container = document.body;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isEnabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Calculate rotation based on pull distance
  const rotation = isRefreshing ? 0 : (pullDistance / PULL_THRESHOLD) * 360;
  const opacity = Math.min(pullDistance / (PULL_THRESHOLD * 0.5), 1);
  const scale = Math.min(0.5 + (pullDistance / PULL_THRESHOLD) * 0.5, 1);
  const shouldTrigger = pullDistance >= PULL_THRESHOLD;

  if (!isEnabled) {
    return children;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Pull to refresh indicator - positioned at title level */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-50 flex items-center justify-center pointer-events-none"
        style={{
          top: Math.max(32 + pullDistance - 40, 32 - 40), // Start at ~32px (title level)
          opacity: opacity,
          transition: isPulling ? 'none' : 'all 0.3s ease-out',
        }}
        data-onboarding="pull-refresh"
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${
            shouldTrigger || isRefreshing
              ? 'bg-primary-500 text-white'
              : 'bg-white text-surface-600'
          }`}
          style={{
            transform: `scale(${scale})`,
            transition: isPulling ? 'background-color 0.2s' : 'all 0.3s ease-out',
          }}
        >
          <RefreshCw
            className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
            style={{
              transform: isRefreshing ? 'none' : `rotate(${rotation}deg)`,
              transition: isPulling ? 'none' : 'transform 0.3s ease-out',
            }}
          />
        </div>
      </div>

      {/* Children rendered normally - no transform to keep bottom nav in place */}
      {children}
    </div>
  );
}
